import React, { useEffect, useMemo, useRef, useState } from "react";
import { BrowserMultiFormatReader, IScannerControls } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType, NotFoundException } from "@zxing/library";
import { QrCode, Info } from "lucide-react";

export type DocumentScannerProps = {
  onScan: (payload: string) => void;
  disabled?: boolean;
  documentTypeLabel?: string;
  hints?: string[];
  startButtonLabel?: string;
};

export default function DocumentScanner({
  onScan,
  disabled = false,
  documentTypeLabel = "Document",
  hints = [],
  startButtonLabel,
}: DocumentScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const [manualPayload, setManualPayload] = useState("");
  const [active, setActive] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);

  const supportsCamera = useMemo(
    () => typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia,
    [],
  );

  const stopCameraStream = React.useCallback(() => {
    const video = videoRef.current;
    const mediaStream = video?.srcObject as MediaStream | null | undefined;
    mediaStream?.getTracks?.().forEach((track) => track.stop());
    if (video) {
      video.srcObject = null;
    }
    controlsRef.current?.stop?.();
    controlsRef.current = null;
  }, []);

  useEffect(() => {
    if (!active || !supportsCamera) return undefined;

    const hints = new Map<DecodeHintType, unknown>();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]);
    hints.set(DecodeHintType.TRY_HARDER, true);

    const reader = new BrowserMultiFormatReader(hints);
    readerRef.current = reader;
    let stopped = false;
    setScanError(null);

    const startScanning = async () => {
      const videoElement = videoRef.current ?? undefined;
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };
      try {
        const controls = await reader.decodeFromConstraints(constraints, videoElement, (result, err) => {
          if (stopped) return;
          if (result) {
            onScan(result.getText());
            setActive(false);
            stopCameraStream();
          } else if (err && !(err instanceof NotFoundException)) {
            setScanError(err.message || "Unable to read code");
          }
        });
        controlsRef.current = controls;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unable to start camera for scanning";
        setScanError(message);
        stopCameraStream();
      }
    };

    void startScanning();

    return () => {
      stopped = true;
      stopCameraStream();
    };
  }, [active, onScan, stopCameraStream, supportsCamera]);

  // If the scanner is deactivated or the parent hides the component, ensure the camera stops.
  useEffect(() => {
    if (!active) {
      stopCameraStream();
    }
  }, [active, stopCameraStream]);

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setActive((prev) => !prev)}
        disabled={!supportsCamera || disabled}
        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <QrCode className="h-4 w-4" />
        {active ? "Stop live scan" : startButtonLabel ?? "Start QR/Barcode scan"}
      </button>
      {active && (
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <video
            ref={videoRef}
            className="block h-96 w-full bg-black/60 object-cover"
            muted
            playsInline
            autoPlay
          />
        </div>
      )}
      {!supportsCamera && (
        <p className="text-xs text-amber-600">
          Camera access is not available in this browser. Paste the scanned payload instead.
        </p>
      )}
      {scanError && <p className="text-xs text-red-600">{scanError}</p>}
      <div className="space-y-1">
        <label className="text-xs font-medium text-slate-600" htmlFor="manual-payload">
          Paste scanned payload
        </label>
        <textarea
          id="manual-payload"
          value={manualPayload}
          onChange={(event) => setManualPayload(event.target.value)}
          rows={3}
          placeholder="Paste QR/Barcode text here"
          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              if (manualPayload.trim()) {
                onScan(manualPayload.trim());
                setManualPayload("");
              }
            }}
            disabled={!manualPayload.trim()}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Use payload
          </button>
        </div>
      </div>
      {hints.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
            <Info className="h-3.5 w-3.5" />
            SOP checklist highlights ({documentTypeLabel})
          </div>
          <ul className="mt-2 space-y-1 text-xs text-slate-600">
            {hints.map((hint) => (
              <li key={hint} className="flex gap-2">
                <span className="text-slate-400">•</span>
                <span>{hint}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
